import React, {useEffect, useRef, useImperativeHandle, forwardRef} from 'react';
import NavToggleMenuProps, { NavToggleMenuHandle } from "./props";
import logoYWLarge from "../../../icons/LuxYW-name.svg";
import "./styles.scss";
import NavLinks from '../NavLinks/NavLinks';
import LanguageCurrencyToggle from '../LanguageCurrencyPicker/components/LanguageCurrencyToggle/LanguageCurrencyToggle';
import closeIconWhite from "../../../icons/close-white.svg";
import closeIconDark from "../../../icons/close-lux-dark.svg";

const NavToggleMenu = forwardRef<NavToggleMenuHandle, NavToggleMenuProps>((props, ref) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const openMenu = () => {
    menuRef.current?.classList.remove('hidden');
    overlayRef.current?.classList.remove('hidden');
  };

  const setClosingMenuStyles = () => {
    const menu = menuRef.current;
    menu?.classList.add('closing');
    menu?.addEventListener('animationend', () => {
      menu?.classList.add('hidden');
      menu?.classList.remove('closing');
    }, { once: true });
    props.onCloseMenu?.();
  }
  const setClosingOverlayStyles = () => {
    // it will exist for sure. If not, well thrown here
    try {
      const overlay = overlayRef.current!;
      const overlayDisplay = getComputedStyle(overlay).getPropertyValue('display');
      if(overlayDisplay !== 'none') {
        overlay.classList.add('closing');
        overlay.addEventListener('animationend', () => {
          overlay.classList.add('hidden');
          overlay.classList.remove('closing');
          overlay.classList.remove('force');
        }, {once: true});
      } else {
        overlay.classList.add('hidden');
        overlay.classList.remove('closing');
        overlay.classList.remove('force');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error setting closing overlay styles:", error);
    }
  }

  const closeMenu = () => {
    setClosingMenuStyles();
    setClosingOverlayStyles();
  };

  const displayOverlay = () => {
    const overlay = overlayRef.current;
    overlay?.classList.remove('hidden');
    overlay?.classList.remove('closing');
    overlay?.classList.add('force');
  }
  const hideOverlay = () => {
    const menu = menuRef.current;
    setClosingOverlayStyles();
    menu?.classList.add('hidden');
  }

  useImperativeHandle(ref, () => ({
    openMenu,
    closeMenu,
    displayOverlay,
    hideOverlay,
    setClosingOverlayStyles
  }));

  useEffect(() => {
    const overlay = overlayRef.current;
    const handleOverlayClick = (ev: MouseEvent) => {
      props.handleOverlayClick?.(ev);
    };
    overlay?.addEventListener('click', handleOverlayClick);

    return () => {
      overlay?.removeEventListener('click', handleOverlayClick);
    };
  }, [props.handleOverlayClick]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    props.handleNavLinkClick?.(e);
    closeMenu();
  }

  const handleNavSubLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    props.handleNavSubLinkClick?.(e);
    closeMenu();
  }

  const handleToggleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onLanguageCurrencyFullScreenClick?.(e);
    closeMenu();
  }

  const menuClassName = `nav-toggle-menu ${props.initiallyHidden !== false ? ' hidden' : ''}`;
  const overlayClassName = `nav-overlay${props.initiallyHidden !== false ? ' hidden' : ''}`;
  const shouldShowLanguageCurrencyToggle = props.languageCurrencyToggle?.countryName && props.languageCurrencyToggle?.languageName && props.languageCurrencyToggle?.flagCountryUrl;

  return (
    <>
      <div ref={overlayRef} className={overlayClassName}></div>

      <div ref={menuRef} className={menuClassName} data-e2e="nav-toggle-menu">
        <div className="nav-toggle-menu__header">
          <span className="nav-toggle-menu__header-title">{props.headerTitle}</span>
          <button className="nav-toggle-menu__close-btn" onClick={closeMenu} type="button">
            <img src={closeIconDark} alt="Close"/>
          </button>
        </div>
        <div className="nav-toggle-menu__header--mobile">
          <img src={logoYWLarge} alt="Logo" className="nav-toggle-menu__header--mobile-logo"/>
          <button className="nav-toggle-menu__close-btn" onClick={closeMenu} type="button">
            <img src={closeIconWhite} alt="Close" />
          </button>
        </div>
        {shouldShowLanguageCurrencyToggle && <div className="nav-toggle-menu__language-currency-container">
          <div className="nav-toggle-menu__language-currency-toggle">
            <LanguageCurrencyToggle
              flagCountryUrl={props.languageCurrencyToggle?.flagCountryUrl}
              countryName={props.languageCurrencyToggle?.countryName }
              languageName={props.languageCurrencyToggle?.languageName}
              currencyName={props.languageCurrencyToggle?.currencyName}
              hideCurrency={props.languageCurrencyToggle?.hideCurrency || false}
              handleToggle={handleToggleClick}
            />
          </div>
        </div>
        }
        <NavLinks navigationLinks={props.navigationLinks} handleNavLinkClick={handleNavClick} handleNavSubLinkClick={handleNavSubLinkClick} />
      </div>
    </>
  );
});

NavToggleMenu.displayName = 'NavToggleMenu';

export default React.memo(NavToggleMenu);
